import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from './useApi';
import { queryClient } from './react-query';

export type AnnouncementStatus = 'draft' | 'published' | 'retired';

/** Field names match the API (snake_case). Datetimes are ISO strings with a UTC offset. */
export type Announcement = {
  id: string;
  title: string;
  body_html: string;
  audiences: string[];
  status: AnnouncementStatus;
  published_at: string | null;
  retired_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ActiveAnnouncement = {
  announcement: Announcement | null;
  seen: boolean;
};

export type AnnouncementInput = {
  title?: string;
  body_html?: string;
  status?: AnnouncementStatus;
};

const invalidateAnnouncements = () => {
  queryClient.invalidateQueries({ queryKey: ['announcements'] });
  queryClient.invalidateQueries({ queryKey: ['active-announcement'] });
};

export const useAnnouncements = () => {
  const { makeApiRequest } = useApi();
  return useQuery({
    queryKey: ['announcements'],
    queryFn: () => makeApiRequest<Announcement[]>('/announcements', 'GET'),
  });
};

export const useActiveAnnouncement = (enabled: boolean) => {
  const { makeApiRequest } = useApi();
  return useQuery({
    queryKey: ['active-announcement'],
    queryFn: () =>
      makeApiRequest<ActiveAnnouncement>('/announcements/active', 'GET'),
    enabled,
    // A failed banner is not worth retrying; the page works without it.
    retry: false,
    // Always stale, so every window focus checks for a new announcement (the
    // global 5-minute staleTime would skip focuses within 5 minutes of the last
    // check). The banner lives in the layout, so navigating never remounts it:
    // the interval covers a tab that stays focused for a long time.
    staleTime: 0,
    refetchInterval: 15 * 60 * 1000,
  });
};

export const useCreateAnnouncement = () => {
  const { makeApiRequest } = useApi();
  return useMutation({
    mutationFn: ({
      data,
      publish,
    }: {
      data: AnnouncementInput;
      publish: boolean;
    }) =>
      makeApiRequest<Announcement>(
        `/announcements${publish ? '?publish=true' : ''}`,
        'POST',
        data
      ),
    onSuccess: invalidateAnnouncements,
  });
};

export const useUpdateAnnouncement = () => {
  const { makeApiRequest } = useApi();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AnnouncementInput }) =>
      makeApiRequest<Announcement>(`/announcements/${id}`, 'PATCH', data),
    onSuccess: invalidateAnnouncements,
  });
};

export const useDeleteAnnouncement = () => {
  const { makeApiRequest } = useApi();
  return useMutation({
    mutationFn: (id: string) =>
      makeApiRequest(`/announcements/${id}`, 'DELETE'),
    onSuccess: invalidateAnnouncements,
  });
};

export const useDismissAnnouncement = () => {
  const { makeApiRequest } = useApi();
  return useMutation({
    mutationFn: (id: string) =>
      makeApiRequest(`/announcements/${id}/seen`, 'POST'),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['active-announcement'] }),
  });
};
